import React, { useMemo } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as driverSlice from '../../../data/driver.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as providerSlice from '../../../data/provider.slice';
import { useEta } from '../../../hooks/display/useEta';
import reservationUtils from '../../../services/reservationUtils';
import { Reservation, TripLeg } from '../../../data/models/reservation.model';
import MemberTripLegLiveTracking from '../reservation/MemberTripLegLiveTracking';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '../../../data/models/trip-leg-status.model';
import dateUtils from '../../../services/DateUtils';

const PREFIX = 'TripCard';

interface TripCardProps {
  reservation: Reservation;
  legIndex: number;
  interactive?: boolean;
  enableTracking?: boolean;
  fromTripsPage?: boolean;
}

const classes = {
  tripCardContent: `${PREFIX}-trip-card-content`,
  tripRow: `${PREFIX}-trip-row`,
  tripMapHolder: `${PREFIX}-trip-map-holder`,
  tripMapSpacer: `${PREFIX}-trip-map-spacer`,
  tripMapDropShadow: `${PREFIX}-trip-map-drop-shadow`,
  tripMapOverlay: `${PREFIX}-trip-map-overlay`,
  tripOuterHolder: `${PREFIX}-trip-outer-holder`,
  tripHeaderHolder: `${PREFIX}-trip-header-holder`,
  tripItemHeader: `${PREFIX}-trip-item-header`,
  tripContainer: `${PREFIX}-trip-holder`,
  tripItem: `${PREFIX}-trip-item`,
  tripItemHolder: `${PREFIX}-trip-item-holder`,
  sectionLink: `${PREFIX}-section-link`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.tripCardContent}`]: {
    [`& .section-link`]: {
      textDecoration: 'none',
      color: 'unset',
      pointerEvents: 'cursor',
    },
  },

  [`&.${classes.tripRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`&.${classes.tripMapHolder}`]: {
    flex: 1,
    display: 'flex',
  },

  [`&.${classes.tripMapDropShadow}`]: {
    filter: `drop-shadow(0px 2px 7px ${theme.palette.lightActionDisabled15p.main})`,
    width: '100%',
    minWidth: '170px',
    maxWidth: '483px',
    position: 'relative',
    '& .MemberTripLegLiveTracking-container .MuiTypography-root': {
      display: 'none',
    },
  },

  [`&.${classes.tripMapSpacer}`]: {
    flex: 1,
    minWidth: 20,
    [theme.breakpoints.up('md')]: {
      minWidth: 50,
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 75,
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 100,
    },
  },

  [`&.${classes.tripMapOverlay}`]: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    background: 'transparent',
    top: 0,
    zIndex: 1000,
  },

  [`&.${classes.tripOuterHolder}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.1),
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    [`& .${classes.tripItem}:first-of-type`]: {
      marginTop: theme.spacing(1.5),
    },
  },

  [`&.${classes.tripHeaderHolder}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
    },
  },

  [`& .${classes.tripItemHeader}`]: {
    fontFamily: 'Bitter',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '18px',
    padding: 0,
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: 'white',
    },
  },

  [`&.${classes.tripItemHolder}`]: {
    width: '3px',
    height: '100%',
    margin: '0 auto',
    marginTop: theme.spacing(-1.6),
    backgroundImage: "url('/dot-small.svg')",
  },
}));

function TripCard({
  reservation,
  legIndex,
  interactive = false,
  enableTracking = false,
  fromTripsPage = false,
}: TripCardProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const dispatch = useAppDispatch();
  const [activeTrip, setActiveTrip] = useState<TripLeg | null>(null);
  const driverLocationsByTripLegReferenceId = useAppSelector(
    driverSlice.selectDriverLocationsByTripLegReferenceId,
  );
  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const provider = useAppSelector(providerSlice.selectProvider);

  const syncedTripLeg = useMemo(() => {
    if (!activeTrip?.tripLegReferenceID) return null;
    return syncedTripLegs[activeTrip.tripLegReferenceID] || null;
  }, [activeTrip, syncedTripLegs]);

  const driverLocationForTrip = useMemo(() => {
    if (!activeTrip?.tripLegReferenceID) return null;
    return (
      driverLocationsByTripLegReferenceId[activeTrip.tripLegReferenceID] || null
    );
  }, [activeTrip?.tripLegReferenceID, driverLocationsByTripLegReferenceId]);

  const driverEtaSeconds = useMemo(() => {
    if (driverLocationForTrip) {
      return (
        driverLocationForTrip.estimatedTimeOfArrivalSeconds ??
        driverLocationForTrip.eta
      );
    }

    const locations = syncedTripLeg?.tripLegDriverLocations;
    if (locations && locations.length > 0) {
      const lastLocation = locations[locations.length - 1];
      return lastLocation?.estimatedTimeOfArrivalSeconds;
    }

    return undefined;
  }, [driverLocationForTrip, syncedTripLeg?.tripLegDriverLocations]);

  const driverEta = useEta(driverEtaSeconds ?? undefined);

  const getPickupDueDateTime = () => {
    if (reservation.tripLegs[legIndex].pickUpDueDateTime) {
      return dateUtils.formatPickupDateTime(
        reservation.tripLegs[legIndex].pickUpDueDateTime!,
        reservation.tripLegs[legIndex].pickUpAddress.timeZone!,
      );
    } else {
      const pickupDueDate = dateUtils.formatAppointmentDate(
        reservation.tripLegs[legIndex].appointmentDate,
        reservation.tripLegs[legIndex].pickUpAddress.timeZone!,
      );
      return `${pickupDueDate}, ${t('member.pickupWillCall')}`;
    }
  };

  useEffect(() => {
    const providerID = activeTrip?.transportationProviders?.find(
      (p) => p.isSelected,
    )?.transportationProviderID;
    if (providerID) {
      dispatch(providerSlice.fetchProviderAsync(providerID));
    }
  }, [activeTrip]);

  React.useEffect(() => {
    if (reservation) {
      if (
        reservation.tripLegs[legIndex] &&
        reservation.tripLegs[legIndex].status &&
        tripStatusWithEtaList.includes(
          reservation.tripLegs[legIndex].status as TripLegStatus,
        )
      ) {
        const liveTrip = reservation.tripLegs[legIndex];
        setActiveTrip(liveTrip);
        if (liveTrip.tripLegReferenceID) {
          dispatch(
            tripLegSlice.getTripLegDetailsAsync(liveTrip?.tripLegReferenceID!),
          );
        }
      } else {
        setActiveTrip(null);
      }
    }
  }, [reservation]);

  return (
    <>
      {reservation.tripLegs[legIndex] && (
        <Card>
          <CardContent>
            <StyledBox className={classes.tripCardContent}>
              <NavLink
                to={`/trip-details/${reservation.reservationReferenceID}/${legIndex}?fromTripsPage=${fromTripsPage}`}
                className="section-link">
                <StyledBox className={classes.tripRow}>
                  <StyledBox className={classes.tripOuterHolder}>
                    <StyledBox className={classes.tripHeaderHolder}>
                      <Typography className={classes.tripItemHeader}>
                        {`${t('member.trip')} #${reservation.tripLegs[legIndex].tripLegReferenceID}`}
                      </Typography>
                    </StyledBox>
                    <StyledBox className={classes.tripContainer}>
                      <StyledBox className={classes.tripItem}>
                        <WatchLaterOutlinedIcon color="action" />
                        <StyledBox>
                          {enableTracking && activeTrip ? (
                            <Typography variant="body1" color="green">
                              {t(`member.${activeTrip.status}`)}
                              {driverEta && (
                                <StyledBox component="span" className="bold">
                                  {` - ${t('member.eta')} ${driverEta}`}
                                </StyledBox>
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body1">
                              {`${t('member.pickup')}: `}
                              <StyledBox component="span" className="bold">
                                {getPickupDueDateTime()}
                              </StyledBox>
                            </Typography>
                          )}
                        </StyledBox>
                      </StyledBox>

                      {syncedTripLeg && (
                        <StyledBox className={classes.tripItem}>
                          <DirectionsCarFilledOutlinedIcon color="info" />
                          <StyledBox>
                            {syncedTripLeg?.driver && (
                              <Typography variant="body1">
                                <StyledBox component="span" className="bold">
                                  {reservationUtils.getDriverName(
                                    syncedTripLeg.driver,
                                  )}
                                </StyledBox>
                                {syncedTripLeg?.vehicle && (
                                  <StyledBox>
                                    <StyledBox>
                                      {reservationUtils.getVehicleName(
                                        syncedTripLeg.vehicle,
                                      )}
                                    </StyledBox>
                                    {syncedTripLeg?.vehicle?.vehicleModel && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary">
                                        {syncedTripLeg.vehicle.vehicleModel}
                                      </Typography>
                                    )}
                                  </StyledBox>
                                )}
                              </Typography>
                            )}
                            {/* TODO: re-enable provider info after product feedback, 
                                based on ticket F1V2-3108 removed it */}
                            {/* {provider?.transportationProviderName && (
                              <Typography variant="body1">
                                {`${t('tripStatusTrack.provider')}: `}
                                <StyledBox component="span" className="bold">
                                  {provider.transportationProviderName}
                                </StyledBox>
                              </Typography>
                            )} */}
                          </StyledBox>
                        </StyledBox>
                      )}
                      <StyledBox className={classes.tripItem}>
                        <StyledBox>
                          <RoomOutlinedIcon color="primary" />
                          <StyledBox
                            className={classes.tripItemHolder}></StyledBox>
                        </StyledBox>
                        <StyledBox>
                          <Typography variant="body1">
                            {`${t('member.from')}: `}

                            <Box component="span" className="bold">
                              {
                                reservation.tripLegs[legIndex].pickUpAddress
                                  .addressLine1
                              }
                            </Box>
                          </Typography>
                        </StyledBox>
                      </StyledBox>
                      <StyledBox className={classes.tripItem}>
                        <RoomOutlinedIcon color="error" />
                        <StyledBox>
                          <Typography variant="body1">
                            {`${t('member.to_titleCase')}: `}
                            <StyledBox component="span" className="bold">
                              {
                                reservation.tripLegs[legIndex].dropOffAddress
                                  .addressLine1
                              }
                            </StyledBox>
                          </Typography>
                        </StyledBox>
                      </StyledBox>
                    </StyledBox>
                  </StyledBox>
                  {isLarge && reservation.tripLegs.length > 0 && (
                    <StyledBox className={classes.tripMapHolder}>
                      <StyledBox className={classes.tripMapSpacer} />
                      <StyledBox className={classes.tripMapDropShadow}>
                        <MemberTripLegLiveTracking
                          tripLeg={reservation.tripLegs[legIndex]}
                        />
                        {!interactive && (
                          <StyledBox
                            className={classes.tripMapOverlay}></StyledBox>
                        )}
                      </StyledBox>
                    </StyledBox>
                  )}
                </StyledBox>
              </NavLink>
            </StyledBox>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default TripCard;
